import createFetchClient from "openapi-fetch";
import type { paths } from "./generated.ts";

type TenantPath = Extract<keyof paths, `/t/{tenantId}/${string}`>;

export type TenantPaths = Pick<paths, TenantPath>;

export type ConsolePaths = Omit<paths, TenantPath>;

type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

export type PathsShape = object;

type PathsOf<Paths extends PathsShape, M extends HttpMethod> = {
  [P in keyof Paths]: Paths[P] extends { [K in M]: object } ? P : never;
}[keyof Paths];

type OperationOf<
  Paths extends PathsShape,
  P extends keyof Paths,
  M extends HttpMethod,
> = Paths[P] extends {
  [K in M]: infer O;
}
  ? O
  : never;

type QueryOf<O> = O extends { parameters: { query?: infer Q } } ? Q : undefined;

type PathParamsOf<O> = O extends { parameters: { path?: infer P } } ? P : undefined;

type BodyOf<O> = O extends { requestBody?: { content: { "application/json": infer B } } }
  ? B
  : undefined;

type ResponsesOf<O> = O extends { responses: infer R } ? R : never;

type StatusesOf<O, Class extends string> = {
  [S in keyof ResponsesOf<O>]: S extends number
    ? `${S}` extends `${Class}${string}`
      ? S
      : never
    : never;
}[keyof ResponsesOf<O>];

type JsonOf<R> = R extends { content: { "application/json": infer B } } ? B : never;

type OkOf<O> = O extends { responses: { 200: { content: { "application/json": infer R } } } }
  ? R
  : never;

type CodeOf<O> =
  JsonOf<ResponsesOf<O>[StatusesOf<O, "4" | "5">]> extends infer B
    ? B extends { readonly error: { readonly code: infer C extends string } }
      ? C
      : never
    : never;

type ApiRequest<O> = {
  readonly query?: QueryOf<O>;
  readonly path?: PathParamsOf<O>;
  readonly body?: BodyOf<O>;
};

declare const unknownCode: unique symbol;

type UnknownCode = string & { readonly [unknownCode]: true };

export type ApiFailure<C extends string = string> = {
  readonly ok: false;
  readonly status: number;
  readonly code: C | UnknownCode | null;
  readonly body: unknown;
  readonly retryAfterSeconds: number | null;
};

export const UNREACHABLE = 0;

const retryAfterSeconds = (headers: Headers): number | null => {
  const raw = headers.get("retry-after");
  if (raw === null) return null;
  const seconds = Number(raw);
  return Number.isInteger(seconds) && seconds >= 0 ? seconds : null;
};

type ApiSuccess<T> = {
  readonly ok: true;
  readonly status: number;
  readonly data: T;
};

export type ApiResult<T, C extends string = string> = ApiSuccess<T> | ApiFailure<C>;

export type Fetch = (input: Request, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly headers?: Readonly<Record<string, string>>;
  readonly fetch?: Fetch;
}

type Called<O> = Promise<ApiResult<OkOf<O>, CodeOf<O>>>;

export interface ClientOf<Paths extends PathsShape> {
  call<M extends HttpMethod, P extends PathsOf<Paths, M>>(
    method: M,
    path: P,
    request?: ApiRequest<OperationOf<Paths, P, M>>,
  ): Called<OperationOf<Paths, P, M>>;
}

const TENANT_ID_PARAMETER = "tenantId";

type TenantScopedRequest<O> = {
  readonly query?: QueryOf<O>;
  readonly path?: Omit<NonNullable<PathParamsOf<O>>, typeof TENANT_ID_PARAMETER>;
  readonly body?: BodyOf<O>;
};

export type ConsoleCode<M extends HttpMethod, P extends PathsOf<ConsolePaths, M>> = CodeOf<
  OperationOf<ConsolePaths, P, M>
>;

export type AppCode<M extends HttpMethod, P extends PathsOf<TenantPaths, M>> = CodeOf<
  OperationOf<TenantPaths, P, M>
>;

export interface ApiClient {
  call<M extends HttpMethod, P extends PathsOf<TenantPaths, M>>(
    method: M,
    path: P,
    request?: TenantScopedRequest<OperationOf<TenantPaths, P, M>>,
  ): Called<OperationOf<TenantPaths, P, M>>;
}

export interface AppClientOptions extends ApiClientOptions {
  readonly tenantId: string;
}

const PARAMETER = /\{(\w+)\}/g;

class MissingPathParameterError extends Error {
  constructor(path: string, name: string) {
    super(`${path} needs a ${name} path parameter`);
    this.name = "MissingPathParameterError";
  }
}

const requireParameters = (path: string, params: Readonly<Record<string, unknown>>): void => {
  for (const [, name] of path.matchAll(PARAMETER)) {
    if (name !== undefined && (params[name] === undefined || params[name] === null)) {
      throw new MissingPathParameterError(path, name);
    }
  }
};

const asRecord = (value: unknown): Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

type RawRequest = {
  readonly query?: unknown;
  readonly path?: unknown;
  readonly body?: unknown;
};

const codeOf = (body: unknown): string | null => {
  const error = asRecord(body)["error"];
  const code = asRecord(error)["code"];
  return typeof code === "string" ? code : null;
};

const QUERY_SERIALIZER = { array: { style: "form", explode: false } } as const;

type Transport = ReturnType<typeof createFetchClient<Record<string, never>>>;

const transport = (options: ApiClientOptions): Transport => {
  const send = options.fetch;
  return createFetchClient<Record<string, never>>({
    baseUrl: options.baseUrl,
    credentials: "include",
    querySerializer: QUERY_SERIALIZER,
    ...(options.headers === undefined ? {} : { headers: { ...options.headers } }),
    ...(send === undefined ? {} : { fetch: (input: Request) => send(input) }),
  });
};

type LooseRequest = (
  method: HttpMethod,
  path: string,
  init: {
    readonly params: { readonly path: object; readonly query: object };
    readonly body?: unknown;
    readonly parseAs: "text";
  },
) => Promise<{ data?: string; error?: unknown; response: Response }>;

const dispatch = async <T, C extends string>(
  client: Transport,
  method: HttpMethod,
  path: string,
  params: Readonly<Record<string, unknown>>,
  request: RawRequest | undefined,
): Promise<ApiResult<T, C>> => {
  const pathParams = { ...params, ...asRecord(request?.path) };
  requireParameters(path, pathParams);
  const send = client.request as unknown as LooseRequest;
  const { data, error, response } = await send(method, path, {
    params: { path: pathParams, query: asRecord(request?.query) },
    ...(request?.body === undefined ? {} : { body: request.body }),
    parseAs: "text",
  });
  if (!response.ok) {
    const body = error === undefined || error === "" ? null : error;
    return {
      ok: false,
      status: response.status,
      code: codeOf(body) as C | UnknownCode | null,
      body,
      retryAfterSeconds: retryAfterSeconds(response.headers),
    };
  }
  if (data === undefined || data === "")
    return { ok: true, status: response.status, data: null as T };
  try {
    return { ok: true, status: response.status, data: JSON.parse(data) as T };
  } catch {
    return { ok: false, status: UNREACHABLE, code: null, body: data, retryAfterSeconds: null };
  }
};

export const createClient = <Paths extends PathsShape>(
  options: ApiClientOptions,
): ClientOf<Paths> => {
  const client = transport(options);
  return { call: (method, path, request) => dispatch(client, method, String(path), {}, request) };
};

export const createApiClient = (options: AppClientOptions): ApiClient => {
  const client = transport(options);
  const tenant = { [TENANT_ID_PARAMETER]: options.tenantId };
  return {
    call: (method, path, request) => dispatch(client, method, String(path), tenant, request),
  };
};
